import type { FC } from "react";

import type { Dayjs } from "dayjs";

import { swSettings } from "app/common/services/swSettings";
import { isMonthByMonthPresets } from "app/components/dashboard/widget-wizard/utils/dashboardWizardUtils";
import {
    getMaxSelectableDurationRangeInfoMsg,
    getMaxSelectableDurationRangeWarningMsg,
} from "app/pages/website-analysis/WebsiteAnalysisFilters";
import type { IDurationObject } from "app/services/DurationService";
import DurationService, { BasicDurations } from "app/services/DurationService";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { CUSTOM_DURATION_NAME } from "../constants";

import { DurationSelectorWithState } from "./DurationSelectorWithState";

type WizardDurationEntity = {
    customDashboard: any;
    changeDuration(duration: string, customDashboard: unknown, comparedDuration: string | boolean): void;
};

const DashboardWizardDuration: FC<WizardDurationEntity> = ({ customDashboard, changeDuration }) => {
    const maxDurationRange = {
        maxSelectableDurationRangeNumber: swSettings.current.resources.MaxAllowedIntervalRange,
        maxSelectableDurationRangeInfoMsg: getMaxSelectableDurationRangeInfoMsg(),
        maxSelectableDurationRangeWarningMsg: getMaxSelectableDurationRangeWarningMsg(),
    };

    return (
        <div className="duration">
            <h5>
                <I18n>home.dashboards.wizard.duration.title</I18n>
            </h5>
            <DurationSelectorWithState
                key="durationFilter"
                customDashboardState={customDashboard}
                minDate={customDashboard.minDate}
                maxDate={customDashboard.maxDate}
                isDisabled={customDashboard.disableDatepicker}
                presets={preparePresets(
                    customDashboard.availableDurations,
                    true,
                    undefined,
                    customDashboard.component,
                    customDashboard?.disabledDurations,
                )}
                initialPreset={customDashboard.widget.duration}
                initialComparedDuration={customDashboard.widget.comparedDuration || false}
                onSubmit={(duration: string, comparedDuration: string | boolean) => {
                    changeDuration(duration, customDashboard, comparedDuration);
                }}
                compactMode={true}
                maxDurationRange={maxDurationRange.maxSelectableDurationRangeNumber ? maxDurationRange : null}
            />
        </div>
    );
};

export default DashboardWizardDuration;

type DurationVariantEntity = {
    buttonText: string;
    displayText: string;
    enabled: boolean;
    locked: boolean;
    value: string;
};

type DurationPreset = {
    id: null | string;
    text: string;
    disabled: boolean;
    locked: boolean;
    value: IDurationObject<Dayjs>;
    displayText: string;
};

export const preparePresets = (
    availableDurations: DurationVariantEntity[] = [],
    dashboard: boolean = false,
    overridePresets?: string[],
    componentId: string = "",
    disabledDurations: string[] = [],
) => {
    const overriddenPresets = overridePresets ? overridePresets : proNavigator.current().overrideDatepickerPreset;

    const durations = availableDurations.map((duration) =>
        disabledDurations.includes(duration.value) ? { ...duration, enabled: false } : duration,
    );

    const availablePresets: DurationPreset[] = durations.map((duration) => {
        if (overriddenPresets) {
            if (overriddenPresets.indexOf(duration.value) === -1) {
                return {
                    id: duration.value,
                    text: duration.buttonText,
                    disabled: true,
                    locked: false,
                    value: DurationService.getDurationData(duration.value).raw,
                    displayText: duration.displayText,
                };
            }
        }

        return {
            id: duration.value,
            text: duration.buttonText,
            disabled: !duration.enabled,
            locked: duration.locked,
            value: DurationService.getDurationData(duration.value).raw,
            displayText: duration.displayText,
        };
    });

    if (dashboard && !isMonthByMonthPresets(componentId)) {
        const getValue = () => {
            if (!availablePresets.find((preset) => preset.id === BasicDurations.LAST_THREE_MONTHS)?.disabled) {
                return BasicDurations.LAST_THREE_MONTHS;
            } else {
                return availablePresets.find((preset) => !preset.disabled)?.id;
            }
        };

        availablePresets.push({
            id: null,
            text: "Custom range",
            disabled: disabledDurations.includes(CUSTOM_DURATION_NAME),
            value: DurationService.getDurationData(getValue() ?? "").raw,
            displayText: "Custom range",
            locked: false,
        });
    }
    return availablePresets;
};
